import { QrCodeScanner } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useActiveClassroom } from '../../active-simulation/useActiveSimulation';
import { useCheckRole } from '../../auth';
import JoinSimulationDialog from '../../JoinSimulationDialog';
import LandingPageLayout from '../../layouts/LandingPageLayout';
import { queryClient } from '../../queryClient';
import ScheduledExercises from '../../scheduled-exercises/ScheduledExercises';
import { ClassroomStudentStatus, ExerciseState, ExerciseSummary, UserRole } from '../../types';
import { isInstructorClassroom } from '../../utils/activeSimulation';
import { parseTime } from '../../utils/parseTime';
import { useActiveCompany } from '../../utils/useCompany';

const limit = 16;

const Learn = () => {
  useCheckRole([UserRole.Student]);
  const companyId = useActiveCompany()?.companyId;

  const [exerciseState, setExerciseState] = useState(ExerciseState.Active);
  const { activeClassroom } = useActiveClassroom();

  const [showDialog, setShowDialog] = useState(false);

  const [loadingExercises, setLoadingExercises] = useState(true);
  const [exercisesError, setExercisesError] = useState(false);

  const [data, setData] = useState<ExerciseSummary[]>([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (companyId) {
      setLoadingExercises(true);
      fetchData();
    }
  }, [companyId, exerciseState]);

  const fetchData = async () => {
    try {
      const response = await queryClient.fetchQuery<ExerciseSummary[]>({
        queryKey: [
          {
            path: `learn/companies/${companyId}/scheduled-exercises?scheduleState=${exerciseState}&limit=${limit}&skip=${skip}`,
          },
        ],
      });

      setLoadingExercises(false);

      if (response.length > 0) {
        // @ts-ignore
        const parseResponse: ExerciseSummary[] = response?.map(parseTime);
        setData(skip === 0 ? parseResponse : data.concat(parseResponse));
        setSkip(skip + limit);

        if (response.length < limit) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      setLoadingExercises(false);

      setHasMore(false);
      setExercisesError(true);
    }
  };

  return (
    <LandingPageLayout
      actions={
        <>
          <Button
            href="/learn/scan-qr"
            variant="outlined"
            startIcon={<QrCodeScanner />}
            sx={{ display: { sm: 'none' } }}
          >
            Scan simulator
          </Button>
          <Button
            variant="contained"
            disabled={
              !isInstructorClassroom(activeClassroom) && activeClassroom?.status === ClassroomStudentStatus.Joined
            }
            onClick={() => setShowDialog(true)}
          >
            Join with code
          </Button>
        </>
      }
      scheduledExercises={
        <InfiniteScroll
          dataLength={data.length}
          next={fetchData}
          hasMore={hasMore}
          loader={
            data.length ? (
              <Typography component="h3" variant="h6" gutterBottom>
                Loading...
              </Typography>
            ) : (
              ''
            )
          }
        >
          <ScheduledExercises
            exercises={data}
            exercisesError={exercisesError}
            loadingExercises={loadingExercises}
            onChange={state => {
              setData([]);
              setSkip(0);
              setHasMore(true);
              setExerciseState(state);
            }}
          />
        </InfiniteScroll>
      }
    >
      <JoinSimulationDialog showDialog={showDialog} reset={() => setShowDialog(false)} />
    </LandingPageLayout>
  );
};

export default Learn;
